import React, { useState } from "react";
import { Table, Message, Icon, Dropdown, Input, Segment, Label } from "semantic-ui-react";
import { ORIGENES } from "../catalogs/instrumentos";
import { herenciaDesactualizada, detectarDivergencias, generarPropuestasDeCorreccion, OPCIONES_RESOLUCION_DIVERGENCIA } from "../adaptadorAgua";
import { useWaterBalanceT } from "../useWaterBalanceT";

const ORIGEN_OPTIONS = Object.values(ORIGENES).map((o) => ({ key: o.id, value: o.id, text: o.nombre }));

// Paso 3 — Entradas heredadas. Solo lectura del volumen (regla dura §1.3: el
// Balance nunca escribe en Agua). Lo único editable acá es la ANOTACIÓN
// propia del Balance (método/calibración, que Agua no registra) y la
// resolución de divergencias contra una lectura de campo.
export function EntradasHeredadasStep({ heredado, waterDoc, anotaciones, onChangeAnotaciones, divergencias, onChangeDivergencias, onChangePropuestas }) {
  const t = useWaterBalanceT();
  const [lecturasCampo, setLecturasCampo] = useState({});

  const RESOLUCION_LABEL = {
    agua_tiene_razon: t("water_balance_agua_right_reason"),
    campo_tiene_razon: t("water_balance_field_right_reason"),
    son_periodos_distintos: t("water_balance_different_periods_reason"),
  };

  const anotacionDe = (sourceId) => (anotaciones || []).find((a) => a.sourceId === sourceId);

  const setAnotacion = (sourceId, campo, val) => {
    const actuales = anotaciones || [];
    const existente = actuales.find((a) => a.sourceId === sourceId);
    const nueva = { ...(existente || { sourceId }), [campo]: val };
    onChangeAnotaciones(existente ? actuales.map((a) => (a.sourceId === sourceId ? nueva : a)) : [...actuales, nueva]);
  };

  const chequearDivergencia = (fuente) => {
    const lecturaCampo = lecturasCampo[fuente.sourceId];
    if (lecturaCampo == null || lecturaCampo === "") return;
    const encontradas = detectarDivergencias([fuente], [{ sourceId: fuente.sourceId, volumeM3: Number(lecturaCampo) }]);
    const actuales = divergencias || [];
    if (encontradas.length === 0) {
      onChangeDivergencias(actuales.filter((d) => d.sourceId !== fuente.sourceId));
      return;
    }
    const nueva = encontradas[0];
    const existente = actuales.find((d) => d.sourceId === fuente.sourceId);
    onChangeDivergencias(existente ? actuales.map((d) => (d.sourceId === fuente.sourceId ? { ...nueva, status: existente.status } : d)) : [...actuales, nueva]);
  };

  const resolverDivergencia = (sourceId, status) => {
    const actualizadas = (divergencias || []).map((d) => (d.sourceId === sourceId ? { ...d, status } : d));
    onChangeDivergencias(actualizadas);

    // "Campo tiene razón" es lo único que genera una propuesta a Agua — el
    // Balance nunca la aplica solo, la deja pendiente de aprobación
    // (regla dura §1.3, ver adaptadorAgua.generarPropuestasDeCorreccion).
    if (status === "campo_tiene_razon" && onChangePropuestas) {
      const divergenciaResuelta = actualizadas.find((d) => d.sourceId === sourceId);
      const [propuesta] = generarPropuestasDeCorreccion([divergenciaResuelta], []);
      onChangePropuestas((prev) => {
        const sinEstaFuente = (prev || []).filter((p) => !(p.type === "corregir_volumen" && p.sourceId === sourceId));
        return propuesta ? [...sinEstaFuente, propuesta] : sinEstaFuente;
      });
    }
  };

  if (!heredado.inputs.length) {
    return (
      <Message warning>
        <Icon name="warning sign" />
        {t("water_balance_no_sources_warning")}
      </Message>
    );
  }

  return (
    <div className="water-balance-step">
      <h3>{t("water_balance_inherited_entries_title")}</h3>
      <Message>{t("water_balance_inherited_entries_hint")}</Message>

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_source")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_volume")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_evidence")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_declared_method")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_calibration_due")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_field_reading")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {heredado.inputs.map((f) => {
            const anotacion = anotacionDe(f.sourceId);
            const campoWater = waterDoc?.[f.sourceId];
            const desactualizado = anotacion && campoWater ? herenciaDesactualizada(anotacion, campoWater) : false;
            const divergencia = (divergencias || []).find((d) => d.sourceId === f.sourceId);
            return (
              <Table.Row key={f.sourceId} warning={desactualizado}>
                <Table.Cell>
                  {f.sourceId}
                  {desactualizado && (
                    <div>
                      <Label size="tiny" color="orange">
                        {t("water_balance_outdated_inheritance")}
                      </Label>
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell>{typeof f.volumeM3 === "number" ? f.volumeM3.toLocaleString("es-AR") : <em>{t("water_balance_unit_error")}</em>}</Table.Cell>
                <Table.Cell>{f.hasEvidence ? <Icon name="check" color="green" /> : <Icon name="minus" color="grey" />}</Table.Cell>
                <Table.Cell>
                  <Dropdown
                    selection
                    compact
                    options={ORIGEN_OPTIONS}
                    value={anotacion?.declaredMethod || f.declaredMethod}
                    onChange={(_, d) => setAnotacion(f.sourceId, "declaredMethod", d.value)}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Input type="date" value={anotacion?.calibrationDue || ""} onChange={(e) => setAnotacion(f.sourceId, "calibrationDue", e.target.value)} />
                </Table.Cell>
                <Table.Cell>
                  <Input
                    type="number"
                    placeholder={t("water_balance_field_reading_placeholder")}
                    value={lecturasCampo[f.sourceId] || ""}
                    onChange={(e) => setLecturasCampo({ ...lecturasCampo, [f.sourceId]: e.target.value })}
                    onBlur={() => chequearDivergencia(f)}
                    action={{ icon: "search", onClick: () => chequearDivergencia(f) }}
                  />
                  {divergencia && (
                    <Segment size="small" color={divergencia.status === "sin_resolver" ? "red" : "green"} style={{ marginTop: 6 }}>
                      <p>{t("water_balance_divergence_summary", { pct: divergencia.deltaPct, valorAgua: divergencia.waterValue, valorCampo: divergencia.fieldValue })}</p>
                      <Dropdown
                        placeholder={t("water_balance_resolve_divergence_placeholder")}
                        selection
                        fluid
                        options={OPCIONES_RESOLUCION_DIVERGENCIA.map((o) => ({ key: o.id, value: o.id, text: RESOLUCION_LABEL[o.id] }))}
                        value={divergencia.status === "sin_resolver" ? undefined : divergencia.status}
                        onChange={(_, d) => resolverDivergencia(f.sourceId, d.value)}
                      />
                    </Segment>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      <Message error={(divergencias || []).some((d) => d.status === "sin_resolver")}>{t("water_balance_divergence_block_hint")}</Message>
    </div>
  );
}

export default EntradasHeredadasStep;
